/**
 * /admin/servers — operator-managed worker allowlist (OCP-66).
 *
 * The OCP's `TenantLifecycleService.stepPickWorker` reads from this
 * table to decide where to place new tenants. Empty here = no tenant
 * provisioning works. Each row maps to a Coolify-known server marked
 * with a pool_tag (`tenant-pool` for the default placement target).
 *
 * Sections:
 * 1. Filter sidebar (pool_tag, status, include decommissioned)
 * 2. Server table with inline status toggle + decommission
 * 3. "Add server" panel — pulls Coolify-candidates dropdown so the
 *    operator can't accidentally type a Coolify UUID by hand
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { PoolTagPicker } from '../components/PoolTagPicker';
import { api, ApiError } from '../lib/api';
import {
  BINDING_LABEL,
  resolveEffectiveCapacity,
  type EffectiveCapacityInputs,
} from '../lib/effective-capacity';
import {
  isRoleLockedByPool,
  presetRoleForPoolTag,
  type ServerRole,
} from '../lib/pool-role';
import { useDemoPoolTag } from '../lib/use-demo-pool-tag';
import { useTransientState } from '../lib/useTransientState';

interface ServerCapacity {
  tenant_count: number;
  max_tenants: number;
  is_full: boolean;
  ram_used_mb_sum: number;
  ram_total_mb: number;
  cpu_pct_avg: number;
  cpu_cores: number;
  free_slots_pct: number;
}

// OCP-293: per-server 7/30d headroom from vps_headroom_summary.
interface HeadroomRow {
  vps_server_id: string;
  ram_p95_7d: number | null;
  headroom_ram_pct: number | null;
  headroom_status: 'green' | 'yellow' | 'red' | 'unknown';
  buckets_7d: number;
}

interface ServerItem {
  id: string;
  coolify_uuid: string | null;
  /** OCP-433: friendly display name (null -> falls back to hostname). */
  name: string | null;
  hostname: string;
  region: string;
  pool_tag: string;
  hardware: string;
  /** OCP-347/373 — tenant | storage | infra (agent-runner future). */
  server_role: ServerRole;
  ram_total_mb: number;
  cpu_cores: number;
  disk_gb: number;
  /** Static hardware-class baseline - NOT the cap the scheduler gates on. See OCP-551. */
  max_tenants: number;
  /** OCP-436: operator HARD cap on the effective tenant count (null = none). */
  max_tenants_override?: number | null;
  /** OCP-436: dynamically-computed effective cap - this is what actually gates placement (null until first compute). */
  effective_max_tenants?: number | null;
  /** OCP-436: traceability blob for the current effective cap (null = never computed). */
  effective_capacity_inputs?: EffectiveCapacityInputs | null;
  status: 'active' | 'draining' | 'decommissioned';
  /** OCP-93: drain flag. False = picker skips this server for new tenants. */
  accepts_new: boolean;
  /** OCP-370 - draining since / drained (no tenant left, safe to decommission) since. */
  drain_started_at?: string | null;
  drain_completed_at?: string | null;
  /** OCP-469: last-seen host-agent build version (from the beacon), null until first beacon. */
  agent_version?: string | null;
  /** OCP-469: the agent version baked into the running api image (convergence target). */
  agent_current_version?: string | null;
  /** OCP-469: true when the last-seen agent is an older SemVer than the baked version. */
  agent_outdated?: boolean;
  /** OCP-535 - Coolify proxy (Traefik) patch level as last read, the fleet's newest, and whether this box is behind. */
  proxy_version?: string | null;
  proxy_latest_version?: string | null;
  proxy_outdated?: boolean;
  proxy_refresh_status?: string | null;
  proxy_refresh_detail?: string | null;
  /** OCP-426: host OS-patch observability. */
  last_patched_at?: string | null;
  last_scanned_at?: string | null;
  packages_available?: number | null;
  security_packages_available?: number | null;
  /** OCP-741 - non-null = the last apt-get update failed; the counts are stale. */
  patch_scan_error?: string | null;
  reboot_pending?: boolean;
  /** OCP-807 - OS release + running kernel (last scan / patch) and the drift against the active fleet. */
  os_release?: string | null;
  os_version_id?: string | null;
  kernel_version?: string | null;
  os_info_at?: string | null;
  os_drift?: { behind: boolean; reasons: string[]; fleet_os_version_id: string | null; fleet_kernel: string | null; kernel_major_minor: string | null; kernel_pending_reboot?: boolean } | null;
  auto_created?: boolean;
  tenant_count?: number;
  capacity?: ServerCapacity;
  created_at: string;
  decommissioned_at: string | null;
}

// OCP-433 - display precedence: show the friendly `name` when set, else the
// real hostname. Used for the table primary line + all flash/error messages.
function serverLabel(s: { name: string | null; hostname: string }): string {
  return s.name?.trim() || s.hostname;
}

// OCP-373 — fleet health (per-mount disk) merged in by server id so a
// storage/infra box shows disk usage instead of a meaningless tenant count.
interface DiskMount {
  mount: string;
  total_gb: number;
  used_gb: number;
  used_pct?: number;
  inode_pct?: number;
}
interface FleetRow {
  id: string;
  diskPct: number | null;
  /** OCP-420 - the mount path diskPct came from, so the badge can label it. */
  diskPctMount?: string | null;
  disks?: DiskMount[];
}

const ROLE_GROUPS: { role: ServerItem['server_role']; label: string }[] = [
  { role: 'tenant', label: 'Tenant pool' },
  { role: 'storage', label: 'Storage' },
  { role: 'infra', label: 'Infra' },
];

interface CoolifyCandidate {
  coolify_uuid: string;
  name: string;
  ip: string;
  region: string | null;
  // OCP-359: hardware Coolify already knows — auto-filled into the form.
  architecture: 'x86_64' | 'arm64' | 'unknown';
  cpu_cores: number | null;
  ram_total_mb: number | null;
}

interface Filters {
  pool_tag: string;
  status: '' | 'active' | 'draining' | 'decommissioned';
  include_decommissioned: boolean;
}

const EMPTY_FILTERS: Filters = { pool_tag: '', status: '', include_decommissioned: false };

export function AdminServersPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<ServerItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // OCP-445: non-blocking notice (e.g. a registered region with no timezone mapping).
  const [warning, setWarning] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useTransientState<string | null>(null, 4000);
  const [showAddForm, setShowAddForm] = useState(false);
  // OCP-108: "Add VPS" modal - the operator-facing onboarding surface for
  // a hand-acquired box, separate from the legacy "+ Add server"
  // coolify-allowlist form. OCP-552 removed its Hetzner-provisioning tab, so
  // the modal now has a single mode: register an existing VPS.
  const [showAddVps, setShowAddVps] = useState(false);
  const [headroom, setHeadroom] = useState<Record<string, HeadroomRow>>({});
  const [fleet, setFleet] = useState<Record<string, FleetRow>>({});
  // OCP-426: fleet OS-patch actions (scan / patch), a single fleet-wide action
  // instead of per-server clicking.
  const [patchBusy, setPatchBusy] = useState<null | 'scan' | 'patch'>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.pool_tag) params.set('pool_tag', filters.pool_tag);
      if (filters.status) params.set('status', filters.status);
      if (filters.include_decommissioned) params.set('include_decommissioned', 'true');
      params.set('with_capacity', 'true');
      const qs = params.toString();
      const r = await api<{ items: ServerItem[] }>(
        `/v1/admin/vps-servers${qs ? `?${qs}` : ''}`,
      );
      setItems(r.items);
      // OCP-293: headroom is best-effort — a stale/empty matview must not
      // break the servers page.
      try {
        const hr = await api<{ items: HeadroomRow[] }>('/v1/admin/telemetry/headroom');
        setHeadroom(Object.fromEntries(hr.items.map((h) => [h.vps_server_id, h])));
      } catch {
        setHeadroom({});
      }
      // OCP-373: per-mount disk for storage/infra rows — best-effort.
      try {
        const fl = await api<{ items: FleetRow[] }>('/v1/admin/host-metrics/fleet');
        setFleet(Object.fromEntries(fl.items.map((f) => [f.id, f])));
      } catch {
        setFleet({});
      }
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load servers (HTTP ${err.status}).`);
      else setError('Could not load servers.');
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = useCallback((msg: string) => {
    setActionMessage(msg);
  }, []);

  const handleStatusToggle = useCallback(
    async (server: ServerItem) => {
      const next = server.status === 'active' ? 'draining' : 'active';
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { status: next },
        });
        flash(`${serverLabel(server)} → ${next}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown error';
        setError(`Could not change status of ${serverLabel(server)} (${msg}).`);
      }
    },
    [load, flash],
  );

  const handleMaxTenantsSave = useCallback(
    async (server: ServerItem, value: number) => {
      if (!Number.isFinite(value) || value < 1 || value > 2000) {
        setError(`max_tenants must be between 1 and 2000 (got ${value}).`);
        return;
      }
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, {
          method: 'PATCH',
          body: { max_tenants: value },
        });
        flash(`${serverLabel(server)} max_tenants → ${value}`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown error';
        setError(`Could not update max_tenants on ${serverLabel(server)} (${msg}).`);
      }
    },
    [load, flash],
  );

  // OCP-93: drain/resume toggle. Distinct from the status toggle
  // (active/draining) — accepts_new=false means "picker skip" but
  // the server stays `status='active'` for health-summary aggregates.
  // Operator uses drain when splitting a Coolify pool or prepping
  // a host for decom without immediately flipping it to 'draining'
  // (which has different operational semantics in OCP-78 dashboards).
  const handleAcceptsNewToggle = useCallback(
    async (server: ServerItem) => {
      const next = !server.accepts_new;
      const verb = next ? 'Resume' : 'Drain';
      try {
        await api(`/v1/admin/vps-servers/${server.id}/accepts-new`, {
          method: 'PATCH',
          body: { accepts_new: next },
        });
        flash(`${serverLabel(server)} → ${verb.toLowerCase()}ed (accepts_new=${next})`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown error';
        setError(`Could not ${verb.toLowerCase()} ${serverLabel(server)} (${msg}).`);
      }
    },
    [load, flash],
  );

  // OCP-426/OCP-503: fleet OS-patch actions. Scan is read-only (safe); patch runs
  // a full `apt-get dist-upgrade` (OCP-503 default - installs kernel updates a
  // plain upgrade keeps back) on every active SSH-configured host (bounded
  // concurrency, NEVER reboots - reboots are the separate per-host,
  // maintenance-window action).
  interface FleetPatchResult {
    concurrency: number;
    count: number;
    results: Array<{
      hostname: string;
      result: { status: string; keptBack?: number | null };
    }>;
  }
  const runFleet = useCallback(
    async (mode: 'scan' | 'patch') => {
      if (patchBusy) return;
      if (mode === 'patch' && !window.confirm(
        'Run a full OS patch (apt-get dist-upgrade, kernel updates included) on every active fleet host? This is IO/CPU-heavy and does NOT reboot - a kernel update sets "reboot pending" per-host for the separate controlled reboot. Continue?',
      )) {
        return;
      }
      setPatchBusy(mode);
      setError(null);
      try {
        const path = mode === 'scan' ? 'patch-scan-fleet' : 'patch-fleet';
        // OCP-483: the fleet routes declare a JSON body schema (FleetPatchBody),
        // so an empty `{}` body is required - a bodyless POST sends no
        // content-type and 400s at validation before the handler runs.
        // OCP-503: dist_upgrade is the default so kernel meta-package updates
        // are actually installed fleet-wide instead of looping "kept back".
        const res = await api<FleetPatchResult>(`/v1/admin/vps-servers/${path}`, {
          method: 'POST',
          body: mode === 'patch' ? { dist_upgrade: true } : {},
        });
        const failed = res.results.filter((r) => r.result.status === 'failed').length;
        // OCP-503: surface hosts left with kept-back (held/phased) packages so a
        // residual is visible, not silently swallowed.
        const keptBackHosts =
          mode === 'patch' ? res.results.filter((r) => (r.result.keptBack ?? 0) > 0).length : 0;
        flash(
          `Fleet ${mode}: ${res.count} host(s)${failed ? `, ${failed} failed` : ''}` +
            `${keptBackHosts ? `, ${keptBackHosts} with kept-back` : ''} (concurrency ${res.concurrency}).`,
        );
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown error';
        setError(`Fleet ${mode} failed (${msg}).`);
      } finally {
        setPatchBusy(null);
      }
    },
    [patchBusy, load, flash],
  );

  const handleDecommission = useCallback(
    async (server: ServerItem) => {
      const ok = window.confirm(
        `Decommission ${serverLabel(server)}?\n\n` +
          `The row stays in the DB (soft-delete) for audit purposes but is excluded from new tenant placements immediately. Existing tenants on this server are NOT affected — they keep running until you move them or the host is shut down.`,
      );
      if (!ok) return;
      try {
        await api(`/v1/admin/vps-servers/${server.id}`, { method: 'DELETE' });
        flash(`${serverLabel(server)} decommissioned`);
        await load();
      } catch (err) {
        const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown error';
        setError(`Could not decommission ${serverLabel(server)} (${msg}).`);
      }
    },
    [load, flash],
  );

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* ---- Filters ---- */}
      <aside className="w-full shrink-0 lg:w-56">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Filter
        </h2>
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">Pool tag</span>
            <input
              type="text"
              value={filters.pool_tag}
              onChange={(e) => setFilters((f) => ({ ...f, pool_tag: e.target.value }))}
              placeholder="any"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">Status</span>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value as Filters['status'] }))
              }
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
            >
              <option value="">Any</option>
              <option value="active">Active</option>
              <option value="draining">Draining</option>
              <option value="decommissioned">Decommissioned</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filters.include_decommissioned}
              onChange={(e) =>
                setFilters((f) => ({ ...f, include_decommissioned: e.target.checked }))
              }
            />
            <span>Show decommissioned</span>
          </label>
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="text-xs text-gray-500 underline-offset-2 hover:underline"
          >
            Reset filters
          </button>
        </div>
      </aside>

      {/* ---- Main ---- */}
      <section className="flex-1 min-w-0">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Servers</h1>
          <div className="flex gap-2">
            {/* OCP-426: fleet OS-patch as a single action, not per-server clicking. */}
            <button
              type="button"
              data-testid="fleet-patch-scan"
              onClick={() => void runFleet('scan')}
              disabled={patchBusy !== null}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
              title="Scan every active host for pending OS + security updates (read-only)."
            >
              {patchBusy === 'scan' ? 'Scanning…' : 'Scan fleet'}
            </button>
            <button
              type="button"
              data-testid="fleet-patch-apply"
              onClick={() => void runFleet('patch')}
              disabled={patchBusy !== null}
              className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 text-sm font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 disabled:opacity-50"
              title="Full apt-get dist-upgrade (kernel updates included) on every active host (no reboot). IO/CPU-heavy; runs at a bounded concurrency."
            >
              {patchBusy === 'patch' ? 'Patching…' : 'Patch fleet'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddVps(true)}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
              title="Register an existing STRATO / Netcup / IONOS / OVH VPS"
            >
              + Add VPS
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              title="Legacy: register a Coolify-known server from the candidate list"
            >
              {showAddForm ? 'Cancel' : 'Register Coolify candidate'}
            </button>
          </div>
        </div>

        {actionMessage && (
          <div
            role="status"
            className="mb-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
          >
            {actionMessage}
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="mb-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {warning && (
          <div
            role="status"
            className="mb-3 flex items-start justify-between gap-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
          >
            <span>{warning}</span>
            <button
              type="button"
              onClick={() => setWarning(null)}
              className="shrink-0 rounded px-1 font-medium hover:bg-amber-100 dark:hover:bg-amber-800/40"
              aria-label="Dismiss warning"
            >
              ×
            </button>
          </div>
        )}

        {showAddForm && (
          <AddServerForm
            onAdded={async (regionWarning) => {
              setShowAddForm(false);
              flash('Server registered');
              // OCP-445: register succeeded; surface an unmapped-region notice if present.
              setWarning(regionWarning ?? null);
              await load();
            }}
            onError={(msg) => setError(msg)}
          />
        )}

        {showAddVps && (
          <AddVpsModal
            onClose={() => setShowAddVps(false)}
            onAdded={async (msg) => {
              setShowAddVps(false);
              flash(msg);
              await load();
            }}
            onAddedKeepOpen={async (msg) => {
              flash(msg);
              await load();
            }}
            onError={(msg) => setError(msg)}
          />
        )}

        {!items && !error && <p className="text-sm text-gray-500">Loading…</p>}

        {items && items.length === 0 && (
          <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-4 py-6 text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium text-gray-800 dark:text-gray-200">
              No servers registered{filters.status || filters.pool_tag ? ' with these filters' : ''}.
            </p>
            {!filters.status && !filters.pool_tag && (
              <p className="mt-1">
                Add at least one server with pool tag <code>tenant-pool</code> before tenant
                provisioning can succeed. Click <strong>+ Add server</strong> above.
              </p>
            )}
          </div>
        )}

        {items && items.length > 0 && (
          <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2">Name / Hostname</th>
                  <th className="px-3 py-2">Region</th>
                  <th className="px-3 py-2">Hardware</th>
                  <th className="px-3 py-2">Pool</th>
                  <th className="px-3 py-2">Capacity</th>
                  <th className="px-3 py-2" title="p95 RAM headroom over the last 7 days">Headroom 7d</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Coolify UUID</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
                {ROLE_GROUPS.filter((g) =>
                  items.some((s) => (s.server_role ?? 'tenant') === g.role),
                ).map((g) => (
                  <Fragment key={g.role}>
                    <tr>
                      <td
                        colSpan={9}
                        className="bg-gray-50 dark:bg-gray-800/60 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                      >
                        {g.label}
                      </td>
                    </tr>
                    {items
                      .filter((s) => (s.server_role ?? 'tenant') === g.role)
                      .map((s) => (
                  <tr key={s.id} className={s.status === 'decommissioned' ? 'opacity-60' : ''}>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                      <Link
                        to={`/servers/${s.id}`}
                        className="text-gray-900 dark:text-gray-100 hover:text-brand-600 hover:underline"
                      >
                        {serverLabel(s)}
                      </Link>
                      {s.auto_created && (
                        <span
                          className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-800"
                          title="Spawned by the retired auto-scaler (OCP-74). Nothing auto-provisions any more - kept as provenance."
                        >
                          auto
                        </span>
                      )}
                      {/* OCP-469: host-agent drift badge - the box's last-seen
                          agent is an older build than the shipped image. Re-run
                          host bootstrap on the server detail page to converge it. */}
                      {s.agent_outdated && (
                        <span
                          className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
                          title={`Host-agent v${s.agent_version ?? '?'} is older than the shipped v${s.agent_current_version ?? '?'}. Open the server + re-run host bootstrap to update it.`}
                        >
                          agent v{s.agent_version}
                        </span>
                      )}
                      {/* OCP-535: proxy drift badge - the box's Coolify proxy (Traefik)
                          patch level is behind the newest one in the fleet, or its
                          last refresh failed. The weekly cycle refreshes it; the
                          server page has the manual refresh. */}
                      {(s.proxy_outdated || s.proxy_refresh_status === 'failed') && (
                        <span
                          className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
                          title={
                            s.proxy_refresh_status === 'failed'
                              ? `The last proxy refresh failed: ${s.proxy_refresh_detail ?? '?'}. Open the server and refresh the proxy, or check the box.`
                              : `Coolify proxy (Traefik) ${s.proxy_version ?? '?'} is behind the newest in the fleet (${s.proxy_latest_version ?? '?'}). The weekly cycle refreshes it; open the server to refresh now.`
                          }
                          data-testid="proxy-badge"
                        >
                          proxy {s.proxy_version ?? '?'}{s.proxy_refresh_status === 'failed' ? ' failed' : ' outdated'}
                        </span>
                      )}
                      {/* OCP-807: OS / kernel drift badge - the box runs an older OS
                          release or kernel than the newest in the fleet (the OCP host
                          sat on 24.04 / 6.8 for weeks while the fleet ran 26.04 / 7.0). */}
                      {s.os_drift?.behind && (
                        <span
                          className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
                          title={`${s.os_release ?? 'OS ?'}, kernel ${s.kernel_version ?? '?'}: ${s.os_drift.reasons.join('; ')}. Patch + reboot it, or release-upgrade it (deploy/runbooks/host-patching.md).`}
                          data-testid="os-drift-badge"
                        >
                          os behind
                        </span>
                      )}
                      {/* OCP-426: pending-reboot badge - a kernel/libc update needs
                          a controlled reboot (inside a maintenance window for a
                          tenant host). Open the server to reboot it. */}
                      {s.reboot_pending && (
                        <span
                          className="ml-2 rounded bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-800 dark:text-red-300"
                          title="A pending OS update needs a controlled reboot. Open the server to reboot it (tenant hosts require an open maintenance window)."
                        >
                          reboot pending
                        </span>
                      )}
                      {/* OCP-426: security-backlog badge - security packages are
                          available (unattended-upgrades should keep this at 0). */}
                      {(s.security_packages_available ?? 0) > 0 && (
                        <span
                          className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
                          title={`${s.security_packages_available} security package(s) available. Run a fleet patch to apply them.`}
                        >
                          {s.security_packages_available} security
                        </span>
                      )}
                      {/* OCP-741: the last apt-get update failed - the counts are stale, not clean. */}
                      {s.patch_scan_error && (
                        <span
                          className="ml-2 rounded bg-red-100 dark:bg-red-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-800 dark:text-red-300"
                          title={`apt-get update failed on the last scan: ${s.patch_scan_error}`}
                        >
                          lists stale
                        </span>
                      )}
                      {/* OCP-433: when a friendly name is set, show the real
                          hostname beneath it as muted secondary detail. */}
                      {s.name?.trim() && (
                        <div className="mt-0.5 font-mono text-[11px] font-normal text-gray-500" title="Real hostname">
                          {s.hostname}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{s.region}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {s.hardware !== 'unknown' ? s.hardware : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-300">
                        {s.pool_tag}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {(s.server_role ?? 'tenant') === 'tenant' ? (
                        <CapacityCell server={s} onMaxTenantsSave={handleMaxTenantsSave} />
                      ) : (
                        <DiskCell fleet={fleet[s.id]} />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <HeadroomCell row={headroom[s.id]} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {s.coolify_uuid ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {s.status !== 'decommissioned' && (
                        <div className="inline-flex gap-2">
                          {/* OCP-93: pool-shard drain — picker-skip without
                              flipping status to draining. Use when splitting
                              a healthy pool that's hitting Coolify's ~200-
                              app sluggish threshold; existing tenants stay,
                              new ones route to a different pool. */}
                          {/* OCP-373: pool-shard drain only applies to tenant
                              hosts — storage/infra take no tenants. */}
                          {(s.server_role ?? 'tenant') === 'tenant' && (
                            <button
                              type="button"
                              data-testid={`accepts-new-toggle-${s.id}`}
                              onClick={() => handleAcceptsNewToggle(s)}
                              className={
                                s.accepts_new
                                  ? 'rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60'
                                  : 'rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-xs text-amber-900 dark:text-amber-200 hover:bg-amber-100'
                              }
                              title={
                                s.accepts_new
                                  ? 'Stop new tenants on this server (pool-shard drain). Existing tenants stay; status stays active.'
                                  : 'Resume accepting new tenants. Picker can place here again.'
                              }
                            >
                              {s.accepts_new ? 'Stop new' : 'Resume new'}
                            </button>
                          )}
                          {!s.accepts_new && (s.drain_started_at || s.drain_completed_at) && (
                            <span
                              data-testid={`drain-state-${s.id}`}
                              className={s.drain_completed_at ? 'ml-1 text-xs text-green-700 dark:text-green-400' : 'ml-1 text-xs text-amber-700 dark:text-amber-300'}
                              title={s.drain_completed_at ? 'No tenant is left on this server - it is safe to decommission it.' : 'New placements are off; the server still holds tenants.'}
                            >
                              {s.drain_completed_at
                                ? `drained ${s.drain_completed_at.slice(0, 10)} - safe to decommission`
                                : `draining since ${(s.drain_started_at ?? '').slice(0, 10)}`}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleStatusToggle(s)}
                            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60"
                            title={
                              s.status === 'active'
                                ? 'Mark draining: signals decom-prep; the server is on its way out + may be alerted on'
                                : 'Mark active: revert decom-prep'
                            }
                          >
                            {s.status === 'active' ? 'Mark draining' : 'Mark active'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDecommission(s)}
                            className="rounded border border-red-300 dark:border-red-700 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50"
                          >
                            Decommission
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---- AddServerForm ----------------------------------------------

interface AddServerFormProps {
  onAdded: (regionWarning?: string | null) => Promise<void>;
  onError: (msg: string) => void;
}

function AddServerForm({ onAdded, onError }: AddServerFormProps) {
  const [candidates, setCandidates] = useState<CoolifyCandidate[] | null>(null);
  const [selected, setSelected] = useState('');
  const [poolTag, setPoolTag] = useState('tenant-pool');
  const [serverRole, setServerRole] = useState<ServerRole>('tenant');
  // OCP-428: live demo pool tag so the picker's demo option tracks the setting.
  const demoPoolTag = useDemoPoolTag();
  const [hardware, setHardware] = useState('');
  const [region, setRegion] = useState('');
  const [ramMb, setRamMb] = useState('');
  const [cpuCores, setCpuCores] = useState('');
  const [diskGb, setDiskGb] = useState('');
  // OCP-436: static uplink rating (Mbit/s). Blank -> setting default (1000).
  const [uplinkMbits, setUplinkMbits] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const candidatesResp = await api<{ items: CoolifyCandidate[] }>(
          '/v1/admin/vps-servers/coolify-candidates',
        );
        setCandidates(candidatesResp.items);
        if (candidatesResp.items.length > 0) setSelected(candidatesResp.items[0].coolify_uuid);
      } catch (err) {
        if (err instanceof ApiError) setLoadError(`Could not list Coolify servers (HTTP ${err.status}).`);
        else setLoadError('Could not list Coolify servers.');
      }
    })();
  }, []);

  // OCP-359: when a candidate is selected, auto-fill region / RAM / CPU
  // from the hardware Coolify already knows, so the operator doesn't type
  // them. Fields stay editable as an override.
  useEffect(() => {
    const c = candidates?.find((x) => x.coolify_uuid === selected);
    if (!c) return;
    if (c.region) setRegion(c.region);
    if (c.ram_total_mb != null) setRamMb(String(c.ram_total_mb));
    if (c.cpu_cores != null) setCpuCores(String(c.cpu_cores));
  }, [selected, candidates]);

  // OCP-365: derive the monitoring role from the chosen pool tag so the
  // operator can't submit an inconsistent pair. Tenant-serving pools force
  // 'tenant' (and the select is locked below); infra/storage pools preset
  // the matching role but stay editable.
  useEffect(() => {
    const preset = presetRoleForPoolTag(poolTag, demoPoolTag);
    if (preset && preset !== serverRole) setServerRole(preset);
    // Only re-run on poolTag / demoPoolTag change; serverRole is
    // intentionally not a dep (would fight the operator's manual pick on
    // unconstrained pools).
  }, [poolTag, demoPoolTag]);

  const roleLocked = isRoleLockedByPool(poolTag, demoPoolTag);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selected) return;
      setSubmitting(true);
      try {
        const resp = await api<{ region_warning?: string | null }>('/v1/admin/vps-servers', {
          method: 'POST',
          body: {
            coolify_uuid: selected,
            pool_tag: poolTag || 'standard',
            server_role: serverRole,
            region: region || undefined,
            hardware: hardware || undefined,
            ram_total_mb: ramMb ? Number(ramMb) : undefined,
            cpu_cores: cpuCores ? Number(cpuCores) : undefined,
            disk_gb: diskGb ? Number(diskGb) : undefined,
            uplink_mbits: uplinkMbits ? Number(uplinkMbits) : undefined,
          },
        });
        await onAdded(resp?.region_warning ?? null);
      } catch (err) {
        if (err instanceof ApiError) {
          const reason = (err.body as { error?: string } | null)?.error;
          if (reason === 'already_registered') {
            onError('This Coolify server is already in the allowlist.');
          } else if (reason === 'unknown_coolify_uuid') {
            onError('Coolify does not know that server — refresh and try again.');
          } else {
            onError(`Could not register server (HTTP ${err.status}).`);
          }
        } else {
          onError('Could not register server.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [selected, poolTag, serverRole, hardware, region, ramMb, cpuCores, diskGb, uplinkMbits, onAdded, onError],
  );

  // OCP-359: the selected candidate drives the auto-filled hardware badge.
  const selectedCandidate = candidates?.find((c) => c.coolify_uuid === selected) ?? null;

  if (loadError) {
    return (
      <div className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
        {loadError}
      </div>
    );
  }

  if (!candidates) {
    return <p className="mb-4 text-sm text-gray-500">Loading Coolify servers…</p>;
  }

  if (candidates.length === 0) {
    return (
      <div className="mb-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
        Every Coolify-known server is already in the allowlist. Register a new server with Coolify
        first (Coolify Manager → Servers → Add new server) — it will then appear here.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-4 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
    >
      <h2 className="mb-3 text-sm font-semibold text-gray-800 dark:text-gray-200">Register a Coolify server</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="col-span-2 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Coolify server</span>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            required
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            {candidates.map((c) => (
              <option key={c.coolify_uuid} value={c.coolify_uuid}>
                {c.name} — {c.ip}
                {c.region ? ` (${c.region})` : ''}
              </option>
            ))}
          </select>
          {selectedCandidate && (
            <span className="mt-1 block text-xs text-gray-500">
              Detected from Coolify:{' '}
              <code>{selectedCandidate.architecture}</code>
              {selectedCandidate.cpu_cores != null && <> · {selectedCandidate.cpu_cores} vCPU</>}
              {selectedCandidate.ram_total_mb != null && (
                <> · {(selectedCandidate.ram_total_mb / 1024).toFixed(0)} GB RAM</>
              )}
              {selectedCandidate.architecture === 'arm64' && (
                <span className="ml-1 text-amber-600 dark:text-amber-400">
                  — arm64: excluded from the tenant pool until arm64 images ship
                </span>
              )}
              . RAM / CPU / region below are pre-filled; override only if needed.
            </span>
          )}
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Pool tag</span>
          <PoolTagPicker
            value={poolTag}
            onChange={setPoolTag}
            demoPoolTag={demoPoolTag}
            selectTestId="add-server-pool-tag"
            customTestId="add-server-pool-tag-custom"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Server role</span>
          <select
            value={serverRole}
            onChange={(e) => setServerRole(e.target.value as ServerRole)}
            disabled={roleLocked}
            data-testid="add-server-role"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {SERVER_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            {roleLocked ? (
              <>
                Locked to <code>tenant</code>: <code>{poolTag}</code> is a
                tenant-serving pool, so its servers must be monitored as tenant hosts.
              </>
            ) : (
              <>
                Monitoring role (separate from pool tag). Pick <code>storage</code> for
                the RustFS / object-store host so disk alerts fire at the stricter bar.
              </>
            )}
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Region (optional)</span>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="auto from Coolify"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Hardware label (optional)</span>
          <input
            type="text"
            value={hardware}
            onChange={(e) => setHardware(e.target.value)}
            placeholder="netcup-vps-8000 / ionos-vps-xl"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">RAM (MB)</span>
          <input
            type="number"
            inputMode="numeric"
            value={ramMb}
            onChange={(e) => setRamMb(e.target.value)}
            placeholder="8192"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">CPU cores</span>
          <input
            type="number"
            inputMode="numeric"
            value={cpuCores}
            onChange={(e) => setCpuCores(e.target.value)}
            placeholder="4"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Disk (GB)</span>
          <input
            type="number"
            inputMode="numeric"
            value={diskGb}
            onChange={(e) => setDiskGb(e.target.value)}
            placeholder="160"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        {/* OCP-436: static uplink rating (Mbit/s) - the network dimension of the
            capacity model. Blank = worker.default_uplink_mbits (1000). */}
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Uplink (Mbit/s)</span>
          <input
            type="number"
            inputMode="numeric"
            value={uplinkMbits}
            onChange={(e) => setUplinkMbits(e.target.value)}
            placeholder="1000"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
      </div>
      <div className="mt-4">
        <button
          type="submit"
          disabled={submitting}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Registering…' : 'Register server'}
        </button>
      </div>
    </form>
  );
}

// ---- CapacityCell ----------------------------------------------

interface CapacityCellProps {
  server: ServerItem;
  onMaxTenantsSave: (server: ServerItem, value: number) => void | Promise<void>;
}

function CapacityCell({ server, onMaxTenantsSave }: CapacityCellProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(server.max_tenants));

  const cap = server.capacity;
  const tenantCount = cap?.tenant_count ?? server.tenant_count ?? 0;
  // OCP-551: the ratio denominator is the EFFECTIVE cap - the number the
  // scheduler actually gates placement on - not the static `max_tenants`
  // hardware-class default. Both directions matter: a demo host can be
  // capped DOWN to 1 while a STRATO host can be raised ABOVE its static
  // baseline, so this must never collapse to Math.min(static, effective).
  const resolved = resolveEffectiveCapacity(server);
  const effectiveTotal = resolved.effective;
  const slotPct = effectiveTotal > 0 ? Math.round((tenantCount / effectiveTotal) * 100) : 0;
  const isCritical = slotPct >= 85;
  const isWarn = !isCritical && slotPct >= 60;
  const slotBarColor = isCritical
    ? 'bg-red-500'
    : isWarn
      ? 'bg-yellow-500'
      : 'bg-green-500';

  const ramTotal = server.ram_total_mb || 0;
  const ramUsed = cap?.ram_used_mb_sum ?? 0;
  const ramPct = ramTotal > 0 ? Math.min(100, Math.round((ramUsed / ramTotal) * 100)) : 0;

  // Only worth a line when the two numbers disagree, or the model has
  // never run for this host - an operator reading "12/12, static 12"
  // twice learns nothing.
  const mismatchCaption = resolved.notYetComputed
    ? `effective not yet evaluated - showing static ${resolved.static}`
    : effectiveTotal !== resolved.static
      ? `static ${resolved.static}${resolved.binding ? ` - ${BINDING_LABEL[resolved.binding]}` : ''}`
      : null;

  return (
    <div className="min-w-[12rem] space-y-1">
      <div className="flex items-center gap-2 text-xs">
        <span
          className="font-mono font-medium text-gray-800 dark:text-gray-200"
          title={resolved.reason ?? undefined}
        >
          {tenantCount}
          <span className="mx-0.5 text-gray-400">/</span>
          {effectiveTotal}
        </span>
        <span className="text-gray-500">tenants</span>
      </div>
      <div
        className="h-1 w-full rounded bg-gray-100 dark:bg-gray-800"
        title={`${slotPct}% of effective slots used (cap ${effectiveTotal})`}
      >
        <div
          className={`h-1 rounded ${slotBarColor}`}
          style={{ width: `${Math.min(100, slotPct)}%` }}
        />
      </div>
      {mismatchCaption && (
        <div className="text-[10px] text-gray-500" title={resolved.reason ?? undefined}>
          {mismatchCaption}
        </div>
      )}
      {cap && (
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span title={`${ramUsed} MB of ${ramTotal} MB used`}>
            RAM {ramPct}%
          </span>
          <span title="Average CPU% across tenants on this server">
            CPU {Math.round(cap.cpu_pct_avg)}%
          </span>
        </div>
      )}
      {/* OCP-551: editing the static baseline is a separate action from the
          ratio above it - the label says what it edits so it stops reading
          as "edit the number that governs placement". */}
      <div className="flex items-center gap-1 text-[10px] text-gray-500">
        <span>static cap</span>
        {editing ? (
          <>
            <input
              type="number"
              value={value}
              min={1}
              max={2000}
              onChange={(e) => setValue(e.target.value)}
              className="w-14 rounded border border-gray-300 dark:border-gray-700 px-1 py-0 text-[10px]"
            />
            <button
              type="button"
              onClick={async () => {
                await onMaxTenantsSave(server, Number(value));
                setEditing(false);
              }}
              className="rounded bg-brand-500 px-1 py-0 text-[10px] text-white"
              title="Save the static hardware-class baseline (vps_servers.max_tenants)"
            >
              save
            </button>
            <button
              type="button"
              onClick={() => {
                setValue(String(server.max_tenants));
                setEditing(false);
              }}
              className="rounded border border-gray-300 dark:border-gray-700 px-1 py-0 text-[10px] text-gray-600 dark:text-gray-400"
            >
              cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setValue(String(server.max_tenants));
              setEditing(true);
            }}
            className="cursor-pointer rounded hover:bg-gray-100 dark:hover:bg-gray-800/60"
            title="Edit the static hardware-class baseline. The scheduler gates placement on the effective cap above, not this value."
          >
            {server.max_tenants} (edit)
          </button>
        )}
      </div>
    </div>
  );
}

// ---- DiskCell (storage/infra) ----------------------------------
// OCP-373 — role-aware capacity for non-tenant boxes: worst-mount disk %
// + per-mount detail (from the fleet-health merge), instead of the
// meaningless "0/N tenants · RAM 0%".
function DiskCell({ fleet }: { fleet?: FleetRow }) {
  const disks = fleet?.disks ?? [];
  if (disks.length === 0 && (fleet?.diskPct ?? null) == null) {
    return <span className="text-xs text-gray-400">— no telemetry</span>;
  }
  // OCP-420 - the badge now shows the AUTO-heuristic / mount_config picked
  // mount (system/tiny partitions excluded), labeled with its path, instead
  // of the raw worst-of-all-mounts that surfaced a 20%-full /boot.
  const worst = fleet?.diskPct ?? null;
  const badgeMount = fleet?.diskPctMount ?? null;
  const color =
    worst == null ? 'bg-gray-300' : worst >= 90 ? 'bg-red-500' : worst >= 75 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="min-w-[12rem] space-y-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-mono font-medium text-gray-800 dark:text-gray-200">
          Disk {worst == null ? '—' : `${worst}%`}
        </span>
        <span className="truncate font-mono text-gray-500" title={badgeMount ?? 'no mount detail'}>
          {badgeMount ? `(${badgeMount})` : 'no mount'}
        </span>
      </div>
      <div
        className="h-1 w-full rounded bg-gray-100 dark:bg-gray-800"
        title={`${worst ?? 0}% used${badgeMount ? ` (${badgeMount})` : ''}`}
      >
        <div className={`h-1 rounded ${color}`} style={{ width: `${Math.min(100, worst ?? 0)}%` }} />
      </div>
      {disks.length > 0 && (
        <div className="space-y-0.5 text-[10px] text-gray-500">
          {disks.map((m) => {
            const pct =
              m.used_pct != null
                ? Math.round(m.used_pct)
                : m.total_gb > 0
                  ? Math.round((m.used_gb / m.total_gb) * 100)
                  : 0;
            const isBadge = m.mount === badgeMount;
            return (
              <div
                key={m.mount}
                className={`flex justify-between gap-2 ${isBadge ? 'font-medium text-gray-700 dark:text-gray-300' : ''}`}
              >
                <span className="truncate font-mono">
                  {isBadge ? '● ' : ''}
                  {m.mount}
                </span>
                <span className="whitespace-nowrap">
                  {m.used_gb}/{m.total_gb} GB · {pct}%
                  {m.inode_pct != null && m.inode_pct >= 80 ? ` · inodes ${Math.round(m.inode_pct)}%` : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- StatusBadge -----------------------------------------------

function StatusBadge({ status }: { status: 'active' | 'draining' | 'decommissioned' }) {
  const styles =
    status === 'active'
      ? 'bg-green-100 text-green-800'
      : status === 'draining'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${styles}`}>{status}</span>
  );
}

// ---- OCP-108: AddVpsModal --------------------------------------
//
// Single-flow modal: register a VPS the operator already acquired by hand
// (IP + SSH creds + provider + cost).
//
// OCP-552 removed the second tab ("Provision new (Hetzner)"): the Hetzner
// Cloud spec-picker, its cost card, the account-cap banner and the project-
// capacity check are gone with the API they drove. Register-existing was
// already the default tab and the path every server in the fleet came in
// through; it is now the only one, so the modal no longer needs tabs.
//
// Safety-checks (all enforced in the modal):
//   1. Cost-display before Submit (operator-input)
//   2. Two-step confirm - Preview → 3s-delay → Submit
//   3. Dry-run toggle - simulate without any API/SSH/DB-mutation
//   4. IP-duplicate-check - preflight call gates Submit

interface AddVpsModalProps {
  onClose: () => void;
  onAdded: (flashMessage: string) => Promise<void>;
  /**
   * OCP-622 - agent-only registration: the modal STAYS OPEN so the operator
   * can copy the one-time install command; the parent only refreshes the list.
   */
  onAddedKeepOpen?: (flashMessage: string) => Promise<void>;
  onError: (msg: string) => void;
}

type ModalStage = 'spec' | 'confirm' | 'progress' | 'done';

// OCP-347/354: monitoring role — distinct from pool_tag (placement). Drives
// role-aware disk alerting: storage/infra hosts alert at a stricter disk bar
// (85%) since RustFS/Postgres filling up is an outage, tenant hosts at 90%.
const SERVER_ROLES: Array<{ value: ServerRole; label: string }> = [
  { value: 'tenant', label: 'tenant — runs customer tenants (disk alert 90%)' },
  { value: 'storage', label: 'storage — RustFS / object store (disk alert 85%)' },
  { value: 'infra', label: 'infra — Postgres / internal services (disk alert 85%)' },
  { value: 'ai-gateway', label: 'ai-gateway - managed-AI LiteLLM host, capacity model (disk alert 85%)' },
  { value: 'ai-backend', label: 'ai-backend - managed-AI model host (vLLM), GPU probe + backend capacity dimension (disk alert 85%)' },
  { value: 'control-plane', label: 'control-plane - the host that runs OCP itself: patched last, delayed reboot inside the window, never placed (pool control-plane)' },
];

/** OCP-622 - how the box is reached: through Coolify (SSH, the default) or by its host-agent beacon only. */
type RegisterMode = 'coolify' | 'agent-only';

// OCP-304: Netcup + IONOS lead — the post-Hetzner tenant-pool baseline
// (Netcup ~€0.63/GB-RAM, IONOS VPS+ as the second provider). Ordered by
// the recommended provider ladder.
// OCP-431: `RegProvider` is the single source of truth for the register-
// existing provider keys. The route zod enum + the vps_servers.provider DB
// CHECK must list exactly these values or a registration cannot round-trip
// (that divergence is what left `netcup` broken until OCP-431 backfilled it).
type RegProvider = 'ionos' | 'netcup' | 'ovh' | 'strato' | 'manual' | 'other';

// Picker options are backed by this constant. `defaultRegion` presets the
// region field on select (epic OCP-416 principle: auto over manual, region
// from provider).
const PROVIDERS: ReadonlyArray<{ value: RegProvider; label: string; defaultRegion?: string }> = [
  { value: 'netcup', label: 'Netcup' },
  { value: 'ionos', label: 'IONOS' },
  { value: 'ovh', label: 'OVH' },
  // STRATO is the chosen bare-metal provider for the tenant + storage
  // pool (OCP-D22). DE-based, so default the region to eu-central.
  { value: 'strato', label: 'STRATO', defaultRegion: 'eu-central' },
  { value: 'manual', label: 'Self-Managed' },
  { value: 'other', label: 'Other' },
];

function AddVpsModal({ onClose, onAdded, onAddedKeepOpen, onError }: AddVpsModalProps) {
  const [stage, setStage] = useState<ModalStage>('spec');
  // OCP-622 - agent-only host: no Coolify, no SSH; the host-agent beacon is
  // the only channel (the LAN-only z440 vLLM embedding backend).
  const [regMode, setRegMode] = useState<RegisterMode>('coolify');
  const [aoHostname, setAoHostname] = useState<string>('');
  const [aoName, setAoName] = useState<string>('');
  const [aoRegion, setAoRegion] = useState<string>('eu-central');
  const [aoPoolTag, setAoPoolTag] = useState<string>('infra');
  const [aoServerRole, setAoServerRole] = useState<ServerRole>('ai-backend');
  const [aoHardware, setAoHardware] = useState<string>('');
  const [aoRamMb, setAoRamMb] = useState<string>('');
  const [aoCores, setAoCores] = useState<string>('');
  const [aoDiskGb, setAoDiskGb] = useState<string>('');
  const [aoMonthlyCost, setAoMonthlyCost] = useState<string>('0');
  const [aoDone, setAoDone] = useState<{ server_id: string; install_command: string; region_warning: string | null } | null>(null);

  // Register existing - the only flow since OCP-552.
  const [regProvider, setRegProvider] = useState<RegProvider>('netcup');
  const [regIp, setRegIp] = useState<string>('');
  const [regRegion, setRegRegion] = useState<string>('');
  const [regPoolTag, setRegPoolTag] = useState<string>('tenant-pool');
  const [regServerRole, setRegServerRole] = useState<ServerRole>('tenant');
  // OCP-427 - auth method for the box being registered. 'key' hands a
  // private key to orboto Control (which passes it to the pool manager);
  // 'password' registers a box reachable only via user+password (a key is
  // generated + installed automatically, no manual pre-install).
  const [regAuthMethod, setRegAuthMethod] = useState<'key' | 'password'>('key');
  const [regSshUser, setRegSshUser] = useState<string>('root');
  const [regSshKey, setRegSshKey] = useState<string>('');
  const [regSshPassword, setRegSshPassword] = useState<string>('');
  const [regMonthlyCost, setRegMonthlyCost] = useState<string>('');
  // OCP-433 - friendly display name (was "provider label"); real host lands in
  // regHostname, which is auto-detected over SSH when left blank.
  const [regName, setRegName] = useState<string>('');
  const [regHostname, setRegHostname] = useState<string>('');
  const [preflightState, setPreflightState] = useState<{
    ip_duplicate: boolean;
    ping_ok: boolean;
    ssh_ok: boolean;
    ran: boolean;
  } | null>(null);

  // Shared
  // OCP-428: live demo pool tag drives the demo option in the pool picker.
  const demoPoolTag = useDemoPoolTag();
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [confirmSecondsLeft, setConfirmSecondsLeft] = useState<number>(3);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [progress, setProgress] = useState<string[]>([]);
  const [doneResult, setDoneResult] = useState<{
    server_id: string;
    status: string;
    // OCP-434 - Docker bootstrap + Coolify validation outcome (register only).
    // OCP-451 - plus the GHCR login + pull-smoke outcome.
    // OCP-462 - plus the branded maintenance-page setup outcome.
    bootstrap?: {
      docker: string;
      validation: string;
      registry_login?: string;
      registry_pull?: string;
      maintenance_page?: string;
      // OCP-470 - privilege-escalation outcome for a non-root SSH user.
      sudo?: string;
      detail: string | null;
    };
  } | null>(null);

  // 3s-delay countdown while in the confirm stage.
  useEffect(() => {
    if (stage !== 'confirm') return;
    setConfirmSecondsLeft(3);
    const interval = setInterval(() => {
      setConfirmSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [stage]);

  const agentOnlyDisabled = useMemo(() => {
    if (!aoHostname.trim() || !aoRegion.trim() || !aoPoolTag.trim()) return true;
    if (aoServerRole === 'tenant') return true;
    return false;
  }, [aoHostname, aoRegion, aoPoolTag, aoServerRole]);

  const submitAgentOnly = useCallback(async () => {
    setSubmitting(true);
    setStage('progress');
    setProgress(['Registering agent-only host…']);
    try {
      const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
      const result = await api<{
        server: { id: string; hostname: string };
        agent_secret: string;
        install_command: string;
        region_warning: string | null;
      }>('/v1/admin/vps-servers/agent-only', {
        method: 'POST',
        body: {
          hostname: aoHostname.trim(),
          name: aoName.trim() || undefined,
          region: aoRegion.trim(),
          pool_tag: aoPoolTag.trim(),
          server_role: aoServerRole,
          hardware: aoHardware.trim() || undefined,
          ram_total_mb: num(aoRamMb),
          cpu_cores: num(aoCores),
          disk_gb: num(aoDiskGb),
          monthly_cost_eur: num(aoMonthlyCost) ?? 0,
        },
      });
      setProgress((p) => [...p, 'Row created (no Coolify, no SSH)', 'Agent secret minted', `Ready (server_id=${result.server.id})`]);
      setAoDone({ server_id: result.server.id, install_command: result.install_command, region_warning: result.region_warning });
      setDoneResult({ server_id: result.server.id, status: 'active' });
      setStage('done');
      const notify = onAddedKeepOpen ?? onAdded;
      await notify(`Registered agent-only host ${result.server.hostname} - run the install command on the box.`);
      if (result.region_warning) onError(result.region_warning);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; message?: string } | null) : null;
      const reason = body?.message ?? body?.error ?? (err instanceof ApiError ? `HTTP ${err.status}` : 'unknown');
      onError(`Register failed: ${reason}`);
      setStage('spec');
    } finally {
      setSubmitting(false);
    }
  }, [aoHostname, aoName, aoRegion, aoPoolTag, aoServerRole, aoHardware, aoRamMb, aoCores, aoDiskGb, aoMonthlyCost, onAdded, onAddedKeepOpen, onError]);

  const registerDisabled = useMemo(() => {
    if (!regIp) return true;
    if (!regRegion) return true;
    if (!regMonthlyCost || Number(regMonthlyCost) <= 0) return true;
    // OCP-427 - password auth requires a user + password; key auth stays
    // optional (falls back to the orboto Control default key).
    if (regAuthMethod === 'password' && (!regSshUser.trim() || !regSshPassword)) return true;
    if (preflightState && preflightState.ip_duplicate) return true;
    return false;
  }, [regIp, regRegion, regMonthlyCost, regAuthMethod, regSshUser, regSshPassword, preflightState]);

  const runPreflight = useCallback(async () => {
    if (!regIp) return;
    setPreflightState(null);
    try {
      const r = await api<{ ip_duplicate: boolean; ping_ok: boolean; ssh_ok: boolean }>(
        '/v1/admin/vps-servers/register-preflight',
        {
          method: 'POST',
          body: { ip: regIp },
        },
      );
      setPreflightState({ ...r, ran: true });
    } catch (err) {
      const msg = err instanceof ApiError ? `HTTP ${err.status}` : 'unknown';
      onError(`Preflight failed (${msg}).`);
    }
  }, [regIp, onError]);

  const submitRegister = useCallback(async () => {
    setSubmitting(true);
    setStage('progress');
    setProgress(['Ping IP…']);
    try {
      const result = await api<{
        server_id: string;
        ip: string;
        coolify_uuid: string;
        status: string;
        provider: string;
        // OCP-433 - display name + the REAL hostname the row ended up with.
        name?: string | null;
        hostname?: string;
        // OCP-434 - Docker bootstrap + Coolify validation outcome.
        // OCP-451 - plus the GHCR login + pull-smoke outcome.
        // OCP-462 - plus the branded maintenance-page setup outcome.
        bootstrap?: {
          docker: string;
          validation: string;
          registry_login?: string;
          registry_pull?: string;
          maintenance_page?: string;
          // OCP-470 - privilege-escalation outcome for a non-root SSH user.
          sudo?: string;
          detail: string | null;
        };
      }>('/v1/admin/vps-servers/register', {
        method: 'POST',
        body: {
          provider: regProvider,
          ip: regIp,
          region: regRegion,
          pool_tag: regPoolTag,
          server_role: regServerRole,
          ssh_user: regSshUser.trim() || undefined,
          // OCP-427 - send exactly one credential based on the auth toggle.
          ...(regAuthMethod === 'password'
            ? { ssh_password: regSshPassword || undefined }
            : { ssh_key: regSshKey || undefined }),
          monthly_cost_eur: Number(regMonthlyCost),
          // OCP-433 - friendly name + optional real-host override (auto-detected
          // over SSH when regHostname is blank).
          name: regName || undefined,
          hostname: regHostname || undefined,
          dry_run: dryRun,
        },
      });
      // OCP-434 - reflect the Docker bootstrap + Coolify validation outcome in
      // the progress log so the operator sees the box was made Docker-ready.
      const boot = result.bootstrap;
      const bootLine = boot
        ? boot.docker === 'installed'
          ? 'Docker installed'
          : boot.docker === 'already-present'
            ? 'Docker already present'
            : boot.docker === 'failed'
              ? 'Docker bootstrap FAILED'
              : 'Docker bootstrap skipped'
        : null;
      const validateLine = boot
        ? boot.validation === 'validated'
          ? 'Coolify validated (usable)'
          : boot.validation === 'pending'
            ? 'Coolify validation pending'
            : boot.validation === 'failed'
              ? 'Coolify validation FAILED'
              : null
        : null;
      // OCP-433 - surface the SSH-auto-detected real hostname when the operator
      // left the hostname field blank (so they see what landed in the host field).
      const hostnameLine =
        !dryRun && !regHostname && result.hostname ? `Hostname detected: ${result.hostname}` : null;
      setProgress((p) => [
        ...p,
        'SSH-Connect OK',
        'Coolify-Registration OK',
        ...(bootLine ? [bootLine] : []),
        ...(hostnameLine ? [hostnameLine] : []),
        ...(validateLine ? [validateLine] : []),
        `Ready (server_id=${result.server_id})`,
      ]);
      setDoneResult({ server_id: result.server_id, status: result.status, bootstrap: result.bootstrap });
      setStage('done');
      // The parent's onAdded closes this modal, so the durable operator-facing
      // surfaces are the flash (success summary) + the page alert (warning).
      const bootSummary =
        boot && boot.docker !== 'skipped'
          ? ` (Docker ${boot.docker === 'already-present' ? 'already present' : boot.docker}, Coolify ${boot.validation})`
          : '';
      const msg = dryRun
        ? `Dry-run completed for ${regProvider} at ${regIp}`
        : `Registered ${regProvider} VPS at ${regIp}${bootSummary}`;
      await onAdded(msg);
      // OCP-434 - a registered-but-not-Docker-ready box must not be swallowed:
      // raise a durable page warning so the operator knows to intervene. Set
      // AFTER onAdded because its list-refetch resets the page error to null.
      if (boot && (boot.docker === 'failed' || boot.validation === 'failed')) {
        onError(
          `Registered ${regProvider} VPS at ${regIp}, but the box is NOT Docker-ready ` +
            `(Docker: ${boot.docker}, Coolify: ${boot.validation}). Install Docker on it ` +
            `(curl -fsSL https://get.docker.com | sh) and re-validate the server in Coolify.` +
            (boot.detail ? ` Detail: ${boot.detail}` : ''),
        );
      }
    } catch (err) {
      // OCP-430 - surface the pipeline step that actually failed + the real
      // message, not just "HTTP {status}". The register route returns
      // { failure_step, message } for every VpsRegistrarError (and a Coolify
      // rejection now arrives as a 422/coolify_error with the detail, not an
      // opaque 502), so the operator sees e.g. "[coolify-add] Coolify rejected
      // the new server: ...".
      const body =
        err instanceof ApiError
          ? (err.body as { failure_step?: string; message?: string } | null)
          : null;
      const step = body?.failure_step ? `[${body.failure_step}] ` : '';
      const reason = body?.message ?? (err instanceof ApiError ? `HTTP ${err.status}` : 'unknown');
      onError(`Register failed: ${step}${reason}`);
      setStage('spec');
    } finally {
      setSubmitting(false);
    }
  }, [regProvider, regIp, regRegion, regPoolTag, regServerRole, regAuthMethod, regSshUser, regSshKey, regSshPassword, regMonthlyCost, regName, regHostname, dryRun, onAdded, onError]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-vps-modal-heading"
    >
      <div className="w-full max-w-2xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 px-5 py-3">
          <h2 id="add-vps-modal-heading" className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Add VPS
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {/* OCP-622 - reach mode: Coolify-managed (SSH) or agent-only (beacon). */}
          {stage === 'spec' && (
            <div className="mb-4 flex gap-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm" role="radiogroup" aria-label="How orboto Control reaches the box">
              <label className="flex items-center gap-2">
                <input type="radio" name="reg-mode" checked={regMode === 'coolify'} onChange={() => setRegMode('coolify')} data-testid="reg-mode-coolify" />
                <span>Coolify-managed VPS (SSH)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="reg-mode" checked={regMode === 'agent-only'} onChange={() => setRegMode('agent-only')} data-testid="reg-mode-agent-only" />
                <span>Agent-only host (no Coolify, no SSH)</span>
              </label>
            </div>
          )}
          {stage === 'spec' && regMode === 'agent-only' && (
            <AgentOnlyForm
              hostname={aoHostname} setHostname={setAoHostname}
              name={aoName} setName={setAoName}
              region={aoRegion} setRegion={setAoRegion}
              poolTag={aoPoolTag} setPoolTag={setAoPoolTag}
              serverRole={aoServerRole} setServerRole={setAoServerRole}
              hardware={aoHardware} setHardware={setAoHardware}
              ramMb={aoRamMb} setRamMb={setAoRamMb}
              cores={aoCores} setCores={setAoCores}
              diskGb={aoDiskGb} setDiskGb={setAoDiskGb}
              monthlyCost={aoMonthlyCost} setMonthlyCost={setAoMonthlyCost}
              demoPoolTag={demoPoolTag}
            />
          )}
          {/* ---- Stage: spec ---- */}
          {stage === 'spec' && regMode === 'coolify' && (
            <RegisterForm
              regProvider={regProvider}
              setRegProvider={setRegProvider}
              regIp={regIp}
              setRegIp={setRegIp}
              regRegion={regRegion}
              setRegRegion={setRegRegion}
              regPoolTag={regPoolTag}
              setRegPoolTag={setRegPoolTag}
              regServerRole={regServerRole}
              setRegServerRole={setRegServerRole}
              regAuthMethod={regAuthMethod}
              setRegAuthMethod={setRegAuthMethod}
              regSshUser={regSshUser}
              setRegSshUser={setRegSshUser}
              regSshKey={regSshKey}
              setRegSshKey={setRegSshKey}
              regSshPassword={regSshPassword}
              setRegSshPassword={setRegSshPassword}
              regMonthlyCost={regMonthlyCost}
              setRegMonthlyCost={setRegMonthlyCost}
              regName={regName}
              setRegName={setRegName}
              regHostname={regHostname}
              setRegHostname={setRegHostname}
              demoPoolTag={demoPoolTag}
              preflightState={preflightState}
              onPreflight={runPreflight}
            />
          )}

          {/* ---- Stage: confirm ---- */}
          {stage === 'confirm' && regMode === 'agent-only' && (
            <ConfirmStage
              dryRun={false}
              secondsLeft={confirmSecondsLeft}
              summary={{
                Mode: 'agent-only (no Coolify, no SSH - beacon is the only channel)',
                Hostname: aoHostname,
                'Name (display)': aoName || '(falls back to hostname)',
                Region: aoRegion,
                'Pool tag': aoPoolTag,
                'Server role': aoServerRole,
                'Monthly cost (EUR)': `€${aoMonthlyCost || '0'}`,
              }}
            />
          )}
          {stage === 'confirm' && regMode === 'coolify' && (
            <ConfirmStage
              dryRun={dryRun}
              secondsLeft={confirmSecondsLeft}
              summary={{
                Provider: regProvider,
                IP: regIp,
                Region: regRegion,
                'Pool tag': regPoolTag,
                'Server role': regServerRole,
                'Monthly cost (EUR)': `€${regMonthlyCost}`,
                'Name (display)': regName || '(falls back to hostname)',
                'Hostname (real host)': regHostname || '(auto-detected via SSH)',
              }}
            />
          )}

          {/* ---- Stage: progress ---- */}
          {stage === 'progress' && (
            <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-4">
              <p className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                {dryRun ? 'Dry-run simulating…' : 'Registering…'}
              </p>
              <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {progress.map((p, i) => (
                  <li key={i} className="font-mono text-xs">
                    {i === progress.length - 1 ? '> ' : '✓ '}
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ---- Stage: done ---- */}
          {stage === 'done' && doneResult && (
            <div className="rounded border border-green-300 bg-green-50 p-4 text-sm text-green-900">
              <p className="font-semibold">
                {dryRun && regMode === 'coolify' ? 'Dry-run completed' : 'Server registered'}
              </p>
              <p className="mt-1 font-mono text-xs">server_id = {doneResult.server_id}</p>
              <p className="mt-1">Status: {doneResult.status}</p>
              {/* OCP-622 - the agent-only install one-liner, shown once (the secret is not stored in clear). */}
              {aoDone && (
                <div className="mt-2" data-testid="agent-only-install">
                  <p className="text-xs">
                    Run this <strong>as root on the box</strong> - it installs the host-agent with the
                    minted secret and the <code>SERVER_ROLE</code>. The secret is shown once; a later
                    reinstall needs a new one (server detail, SSH &amp; host-agent, rotate secret).
                  </p>
                  <pre className="mt-1 overflow-x-auto rounded border border-green-200 bg-white p-2 font-mono text-[11px] text-gray-800">{aoDone.install_command}</pre>
                  <button
                    type="button"
                    onClick={() => { void navigator.clipboard?.writeText(aoDone.install_command); }}
                    className="mt-1 rounded border border-green-400 px-2 py-1 text-xs hover:bg-green-100"
                  >
                    Copy command
                  </button>
                </div>
              )}
              {/* OCP-434 - Docker bootstrap + Coolify validation outcome. */}
              {doneResult.bootstrap && (
                <div className="mt-2">
                  <p className="text-xs">
                    Docker:{' '}
                    <strong>
                      {doneResult.bootstrap.docker === 'installed'
                        ? 'installed by OCP'
                        : doneResult.bootstrap.docker === 'already-present'
                          ? 'already present'
                          : doneResult.bootstrap.docker === 'failed'
                            ? 'FAILED'
                            : 'skipped'}
                    </strong>
                    {' · '}Coolify:{' '}
                    <strong>
                      {doneResult.bootstrap.validation === 'validated'
                        ? 'validated (usable)'
                        : doneResult.bootstrap.validation === 'pending'
                          ? 'validation pending'
                          : doneResult.bootstrap.validation === 'failed'
                            ? 'FAILED'
                            : 'not attempted'}
                    </strong>
                  </p>
                  {/* OCP-451 - GHCR login + pull-smoke outcome. */}
                  {doneResult.bootstrap.registry_login && doneResult.bootstrap.registry_pull && (
                    <p className="mt-1 text-xs">
                      GHCR login:{' '}
                      <strong>
                        {doneResult.bootstrap.registry_login === 'ok'
                          ? 'ok'
                          : doneResult.bootstrap.registry_login === 'failed'
                            ? 'FAILED'
                            : 'skipped'}
                      </strong>
                      {' · '}Image pull:{' '}
                      <strong>
                        {doneResult.bootstrap.registry_pull === 'ok'
                          ? 'ok'
                          : doneResult.bootstrap.registry_pull === 'failed'
                            ? 'FAILED'
                            : 'skipped'}
                      </strong>
                    </p>
                  )}
                  {/* OCP-462 - branded maintenance-page (orboto-maintenance) setup outcome. */}
                  {doneResult.bootstrap.maintenance_page && (
                    <p className="mt-1 text-xs">
                      Maintenance page:{' '}
                      <strong>
                        {doneResult.bootstrap.maintenance_page === 'ok'
                          ? 'ok'
                          : doneResult.bootstrap.maintenance_page === 'failed'
                            ? 'FAILED (non-fatal)'
                            : 'skipped'}
                      </strong>
                    </p>
                  )}
                  {/* OCP-470 - non-root SSH user without passwordless sudo. */}
                  {doneResult.bootstrap.sudo === 'unavailable' && (
                    <p className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                      Privileged steps <strong>could not escalate</strong>: the non-root SSH user
                      lacks passwordless sudo. Grant it <code>NOPASSWD</code> sudo (or register with a
                      root user), then re-run host bootstrap on the server detail page.
                    </p>
                  )}
                  {doneResult.bootstrap.registry_pull === 'failed' && (
                    <p className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                      The worker <strong>could not pull the tenant image</strong>. Set{' '}
                      <code>worker.ghcr_pull_user</code> + <code>GHCR_PULL_TOKEN</code>, then re-run
                      host bootstrap on the server detail page.
                    </p>
                  )}
                  {(doneResult.bootstrap.docker === 'failed' ||
                    doneResult.bootstrap.validation === 'failed') && (
                    <p className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                      The box was registered but is <strong>not Docker-ready</strong>. Install
                      Docker on it (<code>curl -fsSL https://get.docker.com | sh</code>) and
                      re-validate the server in Coolify.
                      {doneResult.bootstrap.detail ? ` Detail: ${doneResult.bootstrap.detail}` : ''}
                    </p>
                  )}
                  {doneResult.bootstrap.docker !== 'failed' &&
                    doneResult.bootstrap.validation === 'pending' && (
                      <p className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                        Docker is ready and Coolify validation was triggered, but the server has not
                        flipped to usable yet. Coolify is finishing in the background - refresh the
                        server detail in a minute.
                      </p>
                    )}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
                >
                  Close
                </button>
                {!dryRun && doneResult.server_id && doneResult.server_id !== '00000000-0000-0000-0000-000000000000' && (
                  <Link
                    to={`/servers/${doneResult.server_id}`}
                    className="rounded border border-brand-500 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50"
                  >
                    Open server detail →
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ---- Footer with Dry-run toggle + actions ---- */}
        {stage === 'spec' && (
          <div className="flex items-center justify-between gap-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-5 py-3">
            {regMode === 'coolify' ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  className="rounded"
                />
                <span title="Simulate the full pipeline without any SSH / Coolify / DB mutation. Audit-log captures the dry-run attempt.">
                  Dry-run (no API / DB writes)
                </span>
              </label>
            ) : (
              <span className="text-xs text-gray-500">No dry-run: the row is created and the agent secret minted in one step.</span>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={regMode === 'agent-only' ? agentOnlyDisabled : registerDisabled}
                onClick={() => setStage('confirm')}
                className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="add-vps-next-button"
              >
                Next: review
              </button>
            </div>
          </div>
        )}

        {stage === 'confirm' && (
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-5 py-3">
            <button
              type="button"
              onClick={() => setStage('spec')}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Back
            </button>
            <button
              type="button"
              disabled={confirmSecondsLeft > 0 || submitting}
              onClick={() => (regMode === 'agent-only' ? submitAgentOnly() : submitRegister())}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="add-vps-submit-button"
            >
              {confirmSecondsLeft > 0
                ? `Confirm in ${confirmSecondsLeft}s…`
                : dryRun && regMode === 'coolify'
                  ? 'Run dry-run'
                  : 'Register now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- AgentOnlyForm (OCP-622) ------------------------------------

interface AgentOnlyFormProps {
  hostname: string; setHostname: (v: string) => void;
  name: string; setName: (v: string) => void;
  region: string; setRegion: (v: string) => void;
  poolTag: string; setPoolTag: (v: string) => void;
  serverRole: ServerRole; setServerRole: (v: ServerRole) => void;
  hardware: string; setHardware: (v: string) => void;
  ramMb: string; setRamMb: (v: string) => void;
  cores: string; setCores: (v: string) => void;
  diskGb: string; setDiskGb: (v: string) => void;
  monthlyCost: string; setMonthlyCost: (v: string) => void;
  demoPoolTag: string;
}

/**
 * OCP-622 - the agent-only registration: a host orboto Control reaches only
 * through the host-agent beacon. No IP / SSH / provider fields; a non-tenant
 * pool + role only (the box can never serve tenants - there is no Coolify).
 */
function AgentOnlyForm(p: AgentOnlyFormProps) {
  const tenantPool = isRoleLockedByPool(p.poolTag, p.demoPoolTag);
  const field = 'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm';
  return (
    <div className="space-y-3" data-testid="agent-only-form">
      <p className="rounded border border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40 p-2 text-xs text-sky-900 dark:text-sky-200">
        For a box that is not in orboto Control's Coolify and that it cannot SSH into (LAN-only, its
        own Coolify) - e.g. the vLLM embedding backend. Telemetry, GPU probe, overload alerts and the
        capacity model work through the beacon; agent install, bootstrap, patching, reboot and disk
        reports do not apply and answer <code>ssh_not_applicable</code>.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Hostname (real host)</span>
          <input type="text" value={p.hostname} onChange={(e) => p.setHostname(e.target.value)} placeholder="z440" className={`${field} font-mono`} data-testid="ao-hostname" required />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Name (display)</span>
          <input type="text" value={p.name} onChange={(e) => p.setName(e.target.value)} placeholder="z440 vLLM embedding host" className={field} data-testid="ao-name" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Region</span>
          <input type="text" value={p.region} onChange={(e) => p.setRegion(e.target.value)} placeholder="eu-central" className={field} data-testid="ao-region" required />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Pool tag (non-tenant)</span>
          <PoolTagPicker value={p.poolTag} onChange={p.setPoolTag} demoPoolTag={p.demoPoolTag} selectTestId="ao-pool-tag" />
          {tenantPool && (
            <span className="mt-1 block text-xs text-red-600">
              <code>{p.poolTag}</code> is a tenant-serving pool - an agent-only host cannot serve tenants. Pick <code>infra</code> or <code>storage</code>.
            </span>
          )}
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Server role</span>
          <select value={p.serverRole} onChange={(e) => p.setServerRole(e.target.value as ServerRole)} className={field} data-testid="ao-server-role">
            {SERVER_ROLES.filter((r) => r.value !== 'tenant').map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            <code>ai-backend</code> for the model host: the beacon's GPU rows feed the backend dimension of the gateway capacity model.
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Hardware class</span>
          <input type="text" value={p.hardware} onChange={(e) => p.setHardware(e.target.value)} placeholder="hp-z440" className={field} data-testid="ao-hardware" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">RAM (MB)</span>
          <input type="number" min={0} value={p.ramMb} onChange={(e) => p.setRamMb(e.target.value)} placeholder="65536" className={field} data-testid="ao-ram-mb" />
          <span className="mt-1 block text-xs text-gray-500">Optional - the first beacon fills RAM in when left blank.</span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">CPU cores</span>
          <input type="number" min={0} value={p.cores} onChange={(e) => p.setCores(e.target.value)} placeholder="12" className={field} data-testid="ao-cores" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Disk (GB)</span>
          <input type="number" min={0} value={p.diskGb} onChange={(e) => p.setDiskGb(e.target.value)} placeholder="1000" className={field} data-testid="ao-disk-gb" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Monthly cost (EUR)</span>
          <input type="number" min={0} step="0.01" value={p.monthlyCost} onChange={(e) => p.setMonthlyCost(e.target.value)} className={field} data-testid="ao-monthly-cost" />
          <span className="mt-1 block text-xs text-gray-500">0 for an owned box.</span>
        </label>
      </div>
    </div>
  );
}

// ---- RegisterForm ----------------------------------------------

interface RegisterFormProps {
  regProvider: RegProvider;
  setRegProvider: (v: RegProvider) => void;
  regIp: string;
  setRegIp: (v: string) => void;
  regRegion: string;
  setRegRegion: (v: string) => void;
  regPoolTag: string;
  setRegPoolTag: (v: string) => void;
  regServerRole: ServerRole;
  setRegServerRole: (v: ServerRole) => void;
  regAuthMethod: 'key' | 'password';
  setRegAuthMethod: (v: 'key' | 'password') => void;
  regSshUser: string;
  setRegSshUser: (v: string) => void;
  regSshKey: string;
  setRegSshKey: (v: string) => void;
  regSshPassword: string;
  setRegSshPassword: (v: string) => void;
  regMonthlyCost: string;
  setRegMonthlyCost: (v: string) => void;
  regName: string;
  setRegName: (v: string) => void;
  regHostname: string;
  setRegHostname: (v: string) => void;
  /** OCP-428 - live `tenant.demo_pool_tag` for the shared pool picker. */
  demoPoolTag: string;
  preflightState: { ip_duplicate: boolean; ping_ok: boolean; ssh_ok: boolean; ran: boolean } | null;
  onPreflight: () => Promise<void>;
}

function RegisterForm(p: RegisterFormProps) {
  // OCP-365: preset the monitoring role from the pool tag; tenant-serving
  // pools lock it to tenant, infra/storage pools preset the matching role
  // but stay editable (both non-tenant roles are valid there).
  const { regPoolTag, regServerRole, setRegServerRole, demoPoolTag } = p;
  useEffect(() => {
    const preset = presetRoleForPoolTag(regPoolTag, demoPoolTag);
    if (preset && preset !== regServerRole) setRegServerRole(preset);
  }, [regPoolTag, demoPoolTag]);
  const roleLocked = isRoleLockedByPool(regPoolTag, demoPoolTag);

  // OCP-431: when the operator picks a provider that carries a default
  // region (STRATO -> eu-central), preset the region field - but only
  // when it is still empty so operator-typed values are never clobbered.
  const { regProvider, regRegion, setRegRegion } = p;
  useEffect(() => {
    const def = PROVIDERS.find((pv) => pv.value === regProvider)?.defaultRegion;
    if (def && !regRegion) setRegRegion(def);
  }, [regProvider]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Provider</span>
          <select
            value={p.regProvider}
            onChange={(e) => p.setRegProvider(e.target.value as RegProvider)}
            data-testid="reg-provider"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            {PROVIDERS.map((pv) => (
              <option key={pv.value} value={pv.value}>
                {pv.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">IP address (IPv4 or IPv6)</span>
          <input
            type="text"
            value={p.regIp}
            onChange={(e) => p.setRegIp(e.target.value)}
            placeholder="198.51.100.5"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-sm"
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Region</span>
          <input
            type="text"
            value={p.regRegion}
            onChange={(e) => p.setRegRegion(e.target.value)}
            placeholder="eu-central"
            list="vps-known-regions"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            required
          />
          <datalist id="vps-known-regions">
            <option value="eu-central" />
            <option value="eu-north" />
            <option value="us-east" />
            <option value="us-west" />
            <option value="ap-singapore" />
          </datalist>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Pool tag</span>
          <PoolTagPicker
            value={p.regPoolTag}
            onChange={p.setRegPoolTag}
            demoPoolTag={demoPoolTag}
            selectTestId="reg-pool-tag"
            customTestId="reg-pool-tag-custom"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Server role</span>
          <select
            value={p.regServerRole}
            onChange={(e) => p.setRegServerRole(e.target.value as ServerRole)}
            disabled={roleLocked}
            data-testid="reg-server-role"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {SERVER_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-gray-500">
            {roleLocked ? (
              <>
                Locked to <code>tenant</code> - <code>{regPoolTag}</code> is a tenant-serving pool.
              </>
            ) : (
              <>
                Monitoring role (separate from pool tag). Pick <code>storage</code> for a
                RustFS / object-store host so disk alerts fire at the stricter bar.
              </>
            )}
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Monthly cost (EUR)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={p.regMonthlyCost}
            onChange={(e) => p.setRegMonthlyCost(e.target.value)}
            placeholder="e.g. 12.50"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
            required
          />
          <span className="mt-1 block text-xs text-gray-500">
            Operator-supplied (provider APIs don't expose pricing).
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Name (display, optional)</span>
          <input
            type="text"
            value={p.regName}
            onChange={(e) => p.setRegName(e.target.value)}
            placeholder="STRATO storage box"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Friendly label shown in the fleet. Falls back to the hostname when blank.
          </span>
        </label>
        <label className="col-span-2 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">Hostname (real host, auto-detected)</span>
          <input
            type="text"
            value={p.regHostname}
            onChange={(e) => p.setRegHostname(e.target.value)}
            placeholder="auto-detected via SSH (hostname -f)"
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-gray-500">
            The box's real host. Leave blank to auto-detect over SSH at register; a value here overrides it.
          </span>
        </label>
        {/* OCP-427 - auth method toggle. Key mode hands a private key to
            orboto Control; password mode registers a box reachable only via
            user+password (a key is generated + installed automatically). */}
        <div className="col-span-2 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-700 dark:text-gray-300">Authentication</span>
            <div className="inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-700">
              <button
                type="button"
                data-testid="reg-auth-key"
                onClick={() => p.setRegAuthMethod('key')}
                className={`px-3 py-1 text-xs font-medium ${
                  p.regAuthMethod === 'key'
                    ? 'bg-brand-500 text-white'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
                }`}
              >
                SSH key
              </button>
              <button
                type="button"
                data-testid="reg-auth-password"
                onClick={() => p.setRegAuthMethod('password')}
                className={`px-3 py-1 text-xs font-medium ${
                  p.regAuthMethod === 'password'
                    ? 'bg-brand-500 text-white'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
                }`}
              >
                User + password
              </button>
            </div>
          </div>

          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">SSH username</span>
            <input
              type="text"
              value={p.regSshUser}
              onChange={(e) => p.setRegSshUser(e.target.value)}
              placeholder="root"
              data-testid="reg-ssh-user"
              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-sm"
            />
          </label>

          {p.regAuthMethod === 'key' ? (
            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                SSH private key (optional - falls back to orboto Control default)
              </span>
              <textarea
                value={p.regSshKey}
                onChange={(e) => p.setRegSshKey(e.target.value)}
                rows={3}
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;…&#10;-----END OPENSSH PRIVATE KEY-----"
                data-testid="reg-ssh-key"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-xs"
              />
              <span className="mt-1 block text-xs text-gray-500">
                Paste the unencrypted PEM private key (not the public key). orboto Control uses
                it to onboard the box and for host automation.
              </span>
            </label>
          ) : (
            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">SSH password</span>
              <input
                type="password"
                value={p.regSshPassword}
                onChange={(e) => p.setRegSshPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                data-testid="reg-ssh-password"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 font-mono text-sm"
              />
              <span className="mt-1 block text-xs text-gray-500">
                For a box reachable via user + password (e.g. a fresh STRATO worker). orboto
                Control connects once with the password, installs a generated key, then manages
                the box key-based. The password is used only for that one-time key install and is
                not stored - only the generated key is kept (encrypted) for host automation.
              </span>
            </label>
          )}
        </div>
      </div>

      <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-800 dark:text-gray-200">Validation preview</span>
          <button
            type="button"
            onClick={p.onPreflight}
            disabled={!p.regIp}
            className="rounded bg-brand-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Run preflight
          </button>
        </div>
        {p.preflightState?.ran && (
          <ul className="mt-2 space-y-1 text-xs">
            <li>
              <span className={p.preflightState.ip_duplicate ? 'text-red-700 dark:text-red-300' : 'text-green-700'}>
                {p.preflightState.ip_duplicate ? '✗' : '✓'}
              </span>{' '}
              IP-duplicate-check: {p.preflightState.ip_duplicate ? 'FAIL — IP already in registry' : 'OK'}
            </li>
            <li>
              <span className={p.preflightState.ping_ok ? 'text-green-700' : 'text-red-700 dark:text-red-300'}>
                {p.preflightState.ping_ok ? '✓' : '✗'}
              </span>{' '}
              Ping: {p.preflightState.ping_ok ? 'OK (TCP port 22 reachable)' : 'FAIL'}
            </li>
            <li>
              <span className={p.preflightState.ssh_ok ? 'text-green-700' : 'text-red-700 dark:text-red-300'}>
                {p.preflightState.ssh_ok ? '✓' : '✗'}
              </span>{' '}
              SSH-connect: {p.preflightState.ssh_ok ? 'OK' : 'FAIL'}
            </li>
            <li className="text-gray-500">
              ⏳ Coolify-Registration: pending (runs on Submit)
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

// ---- ConfirmStage ----------------------------------------------

function ConfirmStage({
  dryRun,
  secondsLeft,
  summary,
}: {
  dryRun: boolean;
  secondsLeft: number;
  summary: Record<string, string>;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Review the configuration before {dryRun ? 'simulating' : 'committing'}. The submit button
        unlocks after a 3-second delay to prevent accidental clicks.
      </p>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3">
        <dl className="grid grid-cols-2 gap-2 text-sm">
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-gray-500">{k}</dt>
              <dd className="font-mono text-gray-900 dark:text-gray-100">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      {dryRun && (
        <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Dry-run mode: the pipeline runs through validation + audit-log but no
          SSH-probe or Coolify-add happens and no <code>vps_servers</code> row is
          inserted.
        </div>
      )}
      <div className="text-xs text-gray-500">
        Submit unlocks in: <span className="font-mono">{secondsLeft}s</span>
      </div>
    </div>
  );
}

function HeadroomCell({ row }: { row?: HeadroomRow }) {
  if (!row || row.headroom_status === 'unknown') {
    return (
      <span className="text-xs text-gray-400" title="No telemetry yet">
        —
      </span>
    );
  }
  const cls =
    row.headroom_status === 'green'
      ? 'bg-emerald-500'
      : row.headroom_status === 'yellow'
        ? 'bg-amber-500'
        : 'bg-red-500';
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300"
      title={`p95 RAM headroom over 7d (${row.buckets_7d} hourly samples)`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />
      {row.headroom_ram_pct != null ? `${Number(row.headroom_ram_pct).toFixed(0)}%` : '—'}
    </span>
  );
}
